<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import ShareLinkInvalidMessage from '@/components/ShareLinkInvalidMessage.vue'
import SharedContentBanner from '@/components/SharedContentBanner.vue'
import TermsOfUseFooter from '@/components/TermsOfUseFooter.vue'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getSharedLibraryGoal } from '@/library/api'
import type { LibraryGoal } from '@/library/library_types'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import AnchorPageLabel from './AnchorPageLabel.vue'
import AnchorPageStandards from './anchor-page/AnchorPageStandards.vue'
import AnchorPageTitle from './anchor-page/AnchorPageTitle.vue'
import { useSavedPageGeneratedAssessmentsLoader } from './assessments/useSavedPageGeneratedAssessments'
import SharedAnchorAnnualGoal from './shared-anchor-page/SharedAnchorAnnualGoal.vue'
import SharedAnchorQuarterlyObjectives from './shared-anchor-page/SharedAnchorQuarterlyObjectives.vue'
import { ANCHOR_PAGE_EVENT_NAMES } from './anchor_page_event_names'
import { useAnchorPageStore } from './anchor_page.store'
import { eachKeyComputed } from './page_event_context'

const props = defineProps<{ uuid: string }>()

const router = useRouter()
const route = useRoute()
const anchorPageStore = useAnchorPageStore()
const { content, hasAccordionObjectives, contentEventContext } = storeToRefs(anchorPageStore)
const contentProfileStore = useContentProfileStore()
const { profileId } = storeToRefs(contentProfileStore)
const userStore = useUserStore()
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()
const sharedCopy = ref<LibraryGoal | null>(null)
const isPageLoaded = ref(false)
const isLinkInvalid = ref(false)
const { fetchGeneratedAssessments } = useSavedPageGeneratedAssessmentsLoader(() => props.uuid, isPageLoaded)

const pageEventContext = computed(() => ({
  ...contentEventContext.value,
  goal_is_library: true,
  is_library: true,
  student_uuid: sharedCopy.value?.student_uuid,
  saved_goal_uuid: props.uuid
}))
const analytics = getAnalyticsTracker().getChildContext(eachKeyComputed(pageEventContext))

const shareLink = computed(
  () => window.location.origin + router.resolve({ name: 'SharedAnchorPage', params: { uuid: props.uuid } }).href
)
// Standards are aligned per content profile, so they show only under the profile the owner saved with.
const isOwnersProfileActive = computed(() => isPageLoaded.value && sharedCopy.value?.user_content_profile_id === profileId.value)
// Vue 2 listed quarterly objectives only for a copy that saved some.
const hasSavedObjectives = computed(() => (sharedCopy.value?.objectives?.length ?? 0) > 0)
const standardCopySource = computed(() => ({ title: sharedCopy.value?.title ?? '', uuid: props.uuid }))

let latestProfileRequest = 0

// Only the latest switch is applied, so a slow answer for the owner's profile cannot outlive leaving the page.
async function showProfile(profileIdToShow: string | undefined) {
  const thisRequest = ++latestProfileRequest
  if (!profileIdToShow) return
  try {
    const profile = await contentProfileStore.getProfileDetail(profileIdToShow)
    if (thisRequest === latestProfileRequest) contentProfileStore.setActiveProfileAndStore(profile)
  } catch (error) {
    console.error('Failed to load the content profile:', error)
  }
}

// The deprecated share URL redirects here, so its view reports the URL the visitor actually followed alongside it.
function redirectedFromContext() {
  const redirectedFrom = route.redirectedFrom?.path
  return redirectedFrom ? { redirected_from: redirectedFrom } : undefined
}

let latestLoad = 0

async function loadPage() {
  const thisLoad = ++latestLoad
  const isSuperseded = () => thisLoad !== latestLoad
  isLinkInvalid.value = false
  isPageLoaded.value = false
  sharedCopy.value = null
  anchorPageStore.clearPage()
  let loaded: LibraryGoal
  try {
    loaded = await getSharedLibraryGoal(props.uuid)
  } catch (error) {
    if (isSuperseded()) return
    console.error(error)
    isLinkInvalid.value = true
    return
  }
  if (isSuperseded()) return
  sharedCopy.value = loaded
  await showProfile(loaded.user_content_profile_id)
  if (isSuperseded()) return
  await anchorPageStore.fetchAnchorPage({ uuid: loaded.goal_uuid ?? '', isDraft: false, savedCopy: loaded })
  if (isSuperseded()) return
  isPageLoaded.value = true
  setTitle(`${loaded.title} | Shared Anchor Page`)
  updateFeedbackContext({ uuid: loaded.uuid, title: loaded.title, subject: content.value?.subject })
  anchorPageStore.fetchAllResources()
  void fetchGeneratedAssessments()
  analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.VIEWED_SHARED_PAGE, redirectedFromContext())
}

watch(() => props.uuid, loadPage, { immediate: true })
// As in Vue 2, a profile switched on this page reloads the standards and staircase it aligns.
watch(profileId, () => {
  if (isPageLoaded.value) void anchorPageStore.fetchStandardsAndStaircase()
})

onBeforeUnmount(() => {
  latestLoad++
  anchorPageStore.clearPage()
  void showProfile(userStore.userContentProfileId)
})
</script>

<template>
  <div>
    <ShareLinkInvalidMessage v-if="isLinkInvalid" class="mt-6">
      This link is either invalid, or the goal is no longer shared by its owner. Ask the owner of the goal for a new shareable link.
    </ShareLinkInvalidMessage>
    <!-- As in Vue 2, the page's frame shows at once and fills in as the copy and then its source arrive. -->
    <template v-else>
      <SharedContentBanner
        v-if="isPageLoaded && sharedCopy"
        content-type="a goal"
        :owner-name="sharedCopy.user_name"
        :last-updated="sharedCopy.updated_at"
        :share-link="shareLink"
        :share-content-type="CONTENT_TYPES.SAVED_SUPER_PAGE"
        :container-class="VUE2_CONTAINER_WIDTHS"
      />
      <CopyTrackWrapper type="shared_superpage" :title="sharedCopy?.title ?? ''" :uuid="uuid" gate-copy data-print-blank-page>
        <div class="container pt-2.5 text-gray-900 print:hidden" :class="VUE2_CONTAINER_WIDTHS" data-testid="shared-anchor-page">
          <div class="md:grid md:grid-cols-12 md:gap-x-8">
            <div class="md:col-span-8">
              <AnchorPageTitle :title="sharedCopy?.title ?? ''" :goal-label="sharedCopy?.title ?? ''" />
              <div class="flex flex-wrap items-baseline gap-1" :class="{ 'mb-2': hasAccordionObjectives }">
                <AnchorPageLabel v-if="!hasAccordionObjectives" />
                <!-- Vue 2's label-success green (#5cb85c) gave white text 2.5:1, so this takes the success token, which passes AA -->
                <span class="rounded-xxs bg-success px-2 py-1 text-xs font-bold leading-none text-white" data-testid="saved-to-label">
                  <span class="material-icons-round align-text-bottom text-xs leading-none" aria-hidden="true">person</span>
                  Saved to <span class="fs-exclude">{{ sharedCopy?.student_name }}</span>
                </span>
              </div>
              <SharedAnchorAnnualGoal :saved-page-uuid="uuid" :saved-title="sharedCopy?.title ?? ''" :saved-goal-text="sharedCopy?.grade_level_goal" />
              <SharedAnchorQuarterlyObjectives v-if="isPageLoaded && !hasAccordionObjectives && hasSavedObjectives" :saved-page-uuid="uuid" />
            </div>
            <div class="md:col-span-4">
              <AnchorPageStandards v-if="isOwnersProfileActive" :standard-copy-source="standardCopySource" />
            </div>
          </div>
          <TermsOfUseFooter />
        </div>
      </CopyTrackWrapper>
    </template>
  </div>
</template>

<style>
/* Vue 2 printed this page blank, app shell included, so the owner's name and the share link never reach paper. A print
   overlay opened from the page (a Student Task) still prints. The content's own print:hidden covers browsers without :has(). */
@media print {
  body:has([data-print-blank-page]):not(:has(> [data-print-overlay])) > * {
    display: none !important;
  }
}
</style>
